import React, { useState, useEffect } from 'react';
import { FileText, Clock, PlayCircle, Search, Sparkles } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { posthog } from 'posthog-js';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { cn } from '../../lib/utils';
import HistoryAskAI from './HistoryAskAI';
import { getUserWatchHistory } from '../../services/watchHistoryService';
import { getUserTemplateHistory } from '../../services/templateHistoryService';
import {
  getFilesBatch,
  type BatchFileInfo,
} from '../../services/fileSystemService';
import { getFileUrl } from '../../lib/routingUtils';
import type { WatchHistoryEvent } from '../../services/watchHistoryService';
import type { TemplateHistoryEvent } from '../../services/templateHistoryService';
import type { FileType } from '../../types';
import type { RootState } from '../../reducers';

interface VideoHistoryItem {
  type: 'video';
  id: string;
  title: string;
  duration: string;
  watchedAt: string;
  thumbnail?: string;
  watchPercentage: number;
  channel: string;
  isShared: boolean;
  owner: string;
  loading?: boolean;
  error?: boolean;
  fileData?: FileType;
  lastWatchedTimestamp?: number;
}

interface TemplateHistoryItem {
  type: 'template';
  templateId: string;
  templateTitle: string;
  fileId: string;
  fileName?: string;
  templateOutput?: string;
  usedAt: string;
  fileData?: FileType;
  loading?: boolean;
  error?: boolean;
}

type HistoryItem = VideoHistoryItem | TemplateHistoryItem;

interface HistoryGrouped {
  [key: string]: HistoryItem[];
}

interface HistoryModalProps {
  open: boolean;
  onClose: () => void;
}

const formatRelativeTime = (dateString: string): string => {
  // Handle empty or invalid date strings
  if (!dateString || dateString.trim() === '') {
    return 'Recently';
  }

  const now = new Date();
  const date = new Date(dateString);

  // Check if date is valid
  if (Number.isNaN(date.getTime())) {
    console.warn('Invalid date string:', dateString);
    return 'Recently';
  }

  const diffInMs = now.getTime() - date.getTime();
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInHours < 1) {
    return 'Just now';
  }
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
  }
  if (diffInDays === 1) {
    return 'Yesterday';
  }
  if (diffInDays < 7) {
    return 'This week';
  }
  if (diffInDays < 14) {
    return 'Last week';
  }
  if (diffInDays < 30) {
    return 'This month';
  }
  return date.toLocaleDateString('en-US', {
    month: 'long',
    year: now.getFullYear() !== date.getFullYear() ? 'numeric' : undefined,
  });
};

const groupHistoryByDate = (
  watchHistory: WatchHistoryEvent[],
  templateHistory: TemplateHistoryEvent[],
): HistoryGrouped => {
  const grouped: HistoryGrouped = {};

  // Process watch history
  watchHistory.forEach((event) => {
    const relativeTime = formatRelativeTime(event.watchedAt);

    let groupKey: string;
    if (relativeTime === 'Just now' || relativeTime.includes('hour')) {
      groupKey = 'Today';
    } else if (relativeTime === 'Recently') {
      groupKey = 'Recent';
    } else {
      groupKey = relativeTime;
    }

    if (!grouped[groupKey]) {
      grouped[groupKey] = [];
    }

    // Create initial video item with loading state
    const isSharedVideo = Boolean(event.shared);
    const videoItem: VideoHistoryItem = {
      type: 'video',
      id: event.recordingId,
      title: `Loading...`, // Will be updated when file details are fetched
      duration: 'Unknown',
      watchedAt: relativeTime,
      thumbnail: undefined,
      watchPercentage: 100,
      channel: isSharedVideo ? 'Shared Recordings' : 'Your Recordings',
      isShared: isSharedVideo,
      owner: isSharedVideo ? 'Loading...' : 'You',
      loading: true,
      lastWatchedTimestamp: event.lastWatchedTimestamp,
    };

    grouped[groupKey].push(videoItem);
  });

  // Process template history
  templateHistory.forEach((event) => {
    const relativeTime = formatRelativeTime(event.usedAt);

    let groupKey: string;
    if (relativeTime === 'Just now' || relativeTime.includes('hour')) {
      groupKey = 'Today';
    } else if (relativeTime === 'Recently') {
      groupKey = 'Recent';
    } else {
      groupKey = relativeTime;
    }

    if (!grouped[groupKey]) {
      grouped[groupKey] = [];
    }

    // Create initial template history item with loading state
    const templateItem: TemplateHistoryItem = {
      type: 'template',
      templateId: event.templateId,
      templateTitle: event.templateTitle,
      fileId: event.fileId,
      fileName: 'Loading...',
      templateOutput: event.templateOutput,
      usedAt: relativeTime,
      loading: true,
    };

    grouped[groupKey].push(templateItem);
  });

  // Sort items within each group by timestamp (most recent first)
  Object.keys(grouped).forEach((groupKey) => {
    grouped[groupKey].sort((a, b) => {
      const getTimestamp = (item: HistoryItem) => {
        if (item.type === 'video') {
          return new Date(
            watchHistory.find((w) => w.recordingId === item.id)?.watchedAt || 0,
          ).getTime();
        }
        return new Date(
          templateHistory.find(
            (t) => t.templateId === item.templateId && t.fileId === item.fileId,
          )?.usedAt || 0,
        ).getTime();
      };
      return getTimestamp(b) - getTimestamp(a);
    });
  });

  return grouped;
};

const HistoryModal: React.FC<HistoryModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [historyByDate, setHistoryByDate] = useState<HistoryGrouped>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAskAI, setShowAskAI] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const navigate = useNavigate();
  const { team, settings } = useSelector((state: RootState) => state);
  const teamId = team.selectedTeam?.teamId;
  const currentUser = settings.user;

  // Function to format duration from seconds to MM:SS format
  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Check if an ID is a shared file (shareId format is much shorter than UUID)
  // UUID format: ae7f9cb6-536b-42a7-863c-272a258c8091 (36 chars)
  // ShareId format: Ai4BhArzl7 (much shorter)
  const isSharedFile = (id: string): boolean => id.length < 20;

  // Process batch file info into display format
  const processFileInfo = (
    recordingId: string,
    fileInfo: BatchFileInfo | null,
  ) => {
    if (!fileInfo) return null;

    const fileOwnerName = fileInfo.owner?.name || fileInfo.recorderName;
    const isOwnedByCurrentUser =
      !fileOwnerName ||
      fileOwnerName === currentUser?.name ||
      fileInfo.recorderEmail === currentUser?.email;

    const isActuallyShared = fileInfo.isShared || false;

    let channel: string;
    if (isActuallyShared) {
      channel = t('history.channels.sharedRecordings', 'Shared Recordings');
    } else if (isOwnedByCurrentUser) {
      channel = t('history.channels.yourRecordings', 'Your Recordings');
    } else {
      channel = t(
        'history.channels.ownerRecordings',
        "{{owner}}'s Recordings",
        {
          owner: fileOwnerName || t('history.owner.unknown', 'Unknown'),
        },
      );
    }

    return {
      title:
        fileInfo.name ||
        t('history.recording.title', {
          id: recordingId.slice(-6),
          defaultValue: `Recording ${recordingId.slice(-6)}`,
        }),
      duration: fileInfo.duration
        ? formatDuration(fileInfo.duration)
        : t('history.loading.duration', 'Unknown'),
      thumbnail: fileInfo.thumbUrl,
      isShared: isActuallyShared,
      owner: isOwnedByCurrentUser
        ? t('history.owner.you', 'You')
        : fileOwnerName || t('history.owner.unknown', 'Unknown'),
      channel,
      fileData: fileInfo as unknown as FileType,
    };
  };

  useEffect(() => {
    if (!open) return;

    const fetchHistory = async () => {
      try {
        setLoading(true);

        // Fetch both watch history and template history in parallel
        const [watchHistoryResponse, templateHistoryResponse] =
          await Promise.all([getUserWatchHistory(), getUserTemplateHistory()]);

        // Process histories - handle cases where either service might not be available
        const watchHistory =
          watchHistoryResponse.success && watchHistoryResponse.data
            ? watchHistoryResponse.data.watchHistory
            : [];
        const templateHistory =
          templateHistoryResponse.success &&
          'data' in templateHistoryResponse &&
          templateHistoryResponse.data
            ? templateHistoryResponse.data.templateHistory
            : [];

        // Only show error if both services failed completely
        if (!watchHistoryResponse.success && !templateHistoryResponse.success) {
          setError('Failed to load history');
          return;
        }

        const grouped = groupHistoryByDate(watchHistory, templateHistory);
        setHistoryByDate(grouped);

        // Collect all file IDs for batch fetching
        const fileIds: string[] = [];
        const shareIds: string[] = [];

        // Categorize video recording IDs
        watchHistory.forEach((event: WatchHistoryEvent) => {
          if (isSharedFile(event.recordingId)) {
            shareIds.push(event.recordingId);
          } else {
            fileIds.push(event.recordingId);
          }
        });

        // Categorize template file IDs
        templateHistory.forEach((event: TemplateHistoryEvent) => {
          if (isSharedFile(event.fileId)) {
            if (!shareIds.includes(event.fileId)) {
              shareIds.push(event.fileId);
            }
          } else if (!fileIds.includes(event.fileId)) {
            fileIds.push(event.fileId);
          }
        });

        // Batch fetch all files in a single API call
        let batchResult: Record<string, BatchFileInfo | null> = {};
        if (fileIds.length > 0 || shareIds.length > 0) {
          try {
            const response = await getFilesBatch({ fileIds, shareIds });
            batchResult = response.files;
          } catch (batchError) {
            console.warn('Failed to batch fetch files:', batchError);
          }
        }

        // Update history items with batch results
        setHistoryByDate((prevGrouped) => {
          const updatedGrouped = { ...prevGrouped };

          // Update video items
          watchHistory.forEach((event: WatchHistoryEvent) => {
            const fileInfo = batchResult[event.recordingId];
            const fileDetails = processFileInfo(event.recordingId, fileInfo);

            Object.keys(updatedGrouped).forEach((groupKey) => {
              const itemIndex = updatedGrouped[groupKey].findIndex(
                (item) =>
                  item.type === 'video' && item.id === event.recordingId,
              );
              if (itemIndex !== -1) {
                if (fileDetails) {
                  const videoItem = updatedGrouped[groupKey][
                    itemIndex
                  ] as VideoHistoryItem;
                  updatedGrouped[groupKey][itemIndex] = {
                    ...videoItem,
                    ...fileDetails,
                    loading: false,
                  };
                } else {
                  // File couldn't be fetched - remove from list (likely deleted)
                  updatedGrouped[groupKey].splice(itemIndex, 1);
                  if (updatedGrouped[groupKey].length === 0) {
                    delete updatedGrouped[groupKey];
                  }
                }
              }
            });
          });

          // Update template items
          templateHistory.forEach((event: TemplateHistoryEvent) => {
            const fileInfo = batchResult[event.fileId];
            const fileDetails = processFileInfo(event.fileId, fileInfo);

            Object.keys(updatedGrouped).forEach((groupKey) => {
              const itemIndex = updatedGrouped[groupKey].findIndex(
                (item) =>
                  item.type === 'template' &&
                  item.fileId === event.fileId &&
                  item.templateId === event.templateId,
              );
              if (itemIndex !== -1) {
                if (fileDetails) {
                  const templateItem = updatedGrouped[groupKey][
                    itemIndex
                  ] as TemplateHistoryItem;
                  updatedGrouped[groupKey][itemIndex] = {
                    ...templateItem,
                    fileName:
                      fileDetails.title ||
                      `Recording ${event.fileId.slice(-6)}`,
                    fileData: fileDetails.fileData,
                    loading: false,
                  };
                } else {
                  // File couldn't be fetched - remove from list (likely deleted)
                  updatedGrouped[groupKey].splice(itemIndex, 1);
                  if (updatedGrouped[groupKey].length === 0) {
                    delete updatedGrouped[groupKey];
                  }
                }
              }
            });
          });

          return updatedGrouped;
        });
      } catch (err) {
        console.error('Error fetching history:', err);
        setError(t('history.errors.loadFailed', 'Failed to load history'));
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [open, t, currentUser]);

  // Media query for mobile detection
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    setIsMobile(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches);
      // Reset to list view when switching to mobile
      if (e.matches) {
        setShowAskAI(false);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const handleHistoryItemClick = (item: HistoryItem) => {
    // Track the history item click event
    posthog.capture('history-item-click', {
      recordingId: item.type === 'video' ? item.id : item.fileId,
      recordingName: item.type === 'video' ? item.title : item.fileName,
      shared: item.type === 'video' ? Boolean(item.isShared) : false,
      historyType: item.type === 'video' ? 'watch-history' : 'template-history',
      owner: item.type === 'video' ? item.owner : 'You',
      duration: item.type === 'video' ? item.duration : 'N/A',
      templateId: item.type === 'template' ? item.templateId : undefined,
      templateTitle: item.type === 'template' ? item.templateTitle : undefined,
      timestamp: new Date().toISOString(),
    });
    if (!teamId) {
      console.error('No team selected');
      return;
    }

    // Close modal before navigating
    onClose();

    if (item.type === 'video') {
      // Build timestamp query param for resume functionality
      const timestampParam =
        item.lastWatchedTimestamp && item.lastWatchedTimestamp > 0
          ? `?t=${Math.floor(item.lastWatchedTimestamp)}`
          : '';

      // If this is a shared video, use full page redirect to avoid hash routing
      if (item.isShared) {
        window.location.href = `/app/v/${item.id}${timestampParam}`;
        return;
      }

      // Regular navigation for non-shared videos
      const fileUrl = getFileUrl({
        teamId,
        parentId: 'default',
        fileId: item.id,
      });
      navigate(`${fileUrl}${timestampParam}`);
    } else {
      // Template item - navigate to the file that was used with this template
      const fileUrl = getFileUrl({
        teamId,
        parentId: 'default',
        fileId: item.fileId,
      });
      navigate(fileUrl);
    }
  };

  // Filter items based on search query and loading state
  const filterItems = (items: HistoryItem[]) => {
    // First filter out loading and error items
    let filtered = items.filter((item) => !item.loading && !item.error);

    // Then apply search filter if there's a query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((item) => {
        const title = item.type === 'video' ? item.title : item.templateTitle;
        const fileName = item.type === 'template' ? item.fileName : '';
        return (
          title.toLowerCase().includes(query) ||
          (fileName && fileName.toLowerCase().includes(query))
        );
      });
    }

    return filtered;
  };

  // Get recent video file IDs (excluding templates) for Ask AI
  const getRecentVideoFileIds = (): string[] => {
    const allItems: HistoryItem[] = [];
    Object.values(historyByDate).forEach((items) => {
      allItems.push(...items);
    });

    // Filter for videos only, exclude loading/error items
    const videoItems = allItems.filter(
      (item): item is VideoHistoryItem =>
        item.type === 'video' && !item.loading && !item.error,
    );

    return videoItems.map((item) => item.id);
  };

  const renderHistoryItem = (item: HistoryItem, index: number) => (
    <button
      key={
        item.type === 'video'
          ? item.id
          : `${item.fileId}-${item.templateId}-${index}`
      }
      onClick={() => handleHistoryItemClick(item)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleHistoryItemClick(item);
        }
      }}
      type="button"
      className="w-full text-left px-6 py-2.5 mb-0.5 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-3"
    >
      {/* Thumbnail */}
      <div className="relative flex-shrink-0">
        {item.type === 'video' && item.thumbnail ? (
          <img
            src={item.thumbnail}
            alt={item.title}
            className="w-12 h-8 object-cover rounded bg-gray-100 dark:bg-gray-800"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const wrapper = target.parentElement!;
              wrapper.className =
                'w-12 h-8 rounded bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0';
              wrapper.innerHTML =
                '<svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/></svg>';
            }}
          />
        ) : (
          <div className="w-12 h-8 rounded bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            {item.type === 'template' ? (
              <FileText className="w-4 h-4 text-gray-400" />
            ) : (
              <PlayCircle className="w-4 h-4 text-gray-400" />
            )}
          </div>
        )}
        {/* Duration overlay - only for videos */}
        {item.type === 'video' &&
          item.duration !== 'Unknown' &&
          !item.loading && (
            <div className="absolute bottom-0.5 right-0.5 bg-black/80 text-white px-1 py-0.5 rounded text-[10px] font-medium">
              {item.duration}
            </div>
          )}
      </div>

      {/* Item info */}
      <div className="flex-1 min-w-0 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm line-clamp-1">
            {(() => {
              if (item.loading) {
                return (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Loading...
                  </span>
                );
              }
              return item.type === 'video' ? item.title : item.templateTitle;
            })()}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {item.type === 'video' ? item.channel : item.fileName}
          </div>
        </div>
        {/* Badges aligned to the right */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {/* Template badge */}
          {item.type === 'template' && (
            <Badge
              variant="secondary"
              className="h-5 px-2 text-[10px] font-normal bg-muted/50 hover:bg-muted/50 rounded-full border-0"
            >
              Template
            </Badge>
          )}
          {/* Shared badge */}
          {item.type === 'video' && item.isShared && (
            <Badge
              variant="secondary"
              className="h-5 px-2 text-[10px] font-normal bg-muted/50 hover:bg-muted/50 rounded-full border-0"
            >
              Shared
            </Badge>
          )}
        </div>
      </div>
    </button>
  );

  const recentVideoFileIds = getRecentVideoFileIds();
  const hasVideoFiles = !loading && !error && recentVideoFileIds.length > 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className={cn(
          'h-[480px] max-h-[80vh] overflow-hidden focus:outline-none focus:ring-0 p-0 gap-0 flex flex-col',
          hasVideoFiles && !isMobile ? 'sm:max-w-[900px]' : 'sm:max-w-[600px]',
        )}
      >
        {/* Search Header */}
        <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-200 dark:border-gray-700">
          <Search className="h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search history..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 bg-transparent outline-none text-base"
            disabled={loading}
          />
          {/* Mobile toggle button for Ask AI - positioned absolutely to match X button */}
          {isMobile && hasVideoFiles && (
            <button
              type="button"
              onClick={() => setShowAskAI(!showAskAI)}
              className="absolute right-12 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            >
              <Sparkles className="h-4 w-4" />
              <span className="sr-only">Toggle Ask AI</span>
            </button>
          )}
        </div>

        {/* Content - Split view when Ask AI is enabled */}
        <div className="flex-1 flex overflow-hidden">
          {/* List View - Hide on mobile when Ask AI is shown */}
          <div
            className={cn(
              'overflow-y-auto overflow-x-hidden',
              hasVideoFiles && !isMobile ? 'flex-1' : 'w-full',
              isMobile && showAskAI ? 'hidden' : '',
            )}
          >
            {(() => {
              if (loading) {
                return (
                  <div className="pt-3">
                    <div className="space-y-2">
                      {Array.from({ length: 8 }, (_, i) => (
                        <div key={i} className="flex gap-3 px-6 py-2.5">
                          <Skeleton className="w-12 h-8 rounded flex-shrink-0" />
                          <div className="flex-1 space-y-1">
                            <Skeleton className="h-4 w-full max-w-[70%]" />
                            <Skeleton className="h-3 w-full max-w-[40%]" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }

              if (error) {
                return (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center space-y-1">
                      <p className="text-sm font-medium text-destructive">
                        {error}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Please try refreshing the page
                      </p>
                    </div>
                  </div>
                );
              }

              if (Object.keys(historyByDate).length === 0) {
                return (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-sm text-gray-500">
                      {searchQuery ? 'No results found' : 'No history yet'}
                    </p>
                  </div>
                );
              }

              const hasVisibleItems = Object.entries(historyByDate).some(
                ([, items]) => filterItems(items).length > 0,
              );

              if (!hasVisibleItems && searchQuery) {
                return (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-sm text-gray-500">No results found</p>
                  </div>
                );
              }

              return (
                <div className="pt-3">
                  {Object.entries(historyByDate)
                    .sort(([dateA], [dateB]) => {
                      const getDatePriority = (dateKey: string) => {
                        if (dateKey === 'Today') return 0;
                        if (dateKey === 'Yesterday') return 1;
                        if (dateKey === 'Recent') return 2;
                        if (dateKey === 'This week') return 3;
                        if (dateKey === 'Last week') return 4;
                        if (dateKey === 'This month') return 5;
                        return 6;
                      };

                      const priorityA = getDatePriority(dateA);
                      const priorityB = getDatePriority(dateB);

                      if (priorityA !== priorityB) {
                        return priorityA - priorityB;
                      }

                      if (priorityA === 6 && priorityB === 6) {
                        const monthOrder = [
                          'January',
                          'February',
                          'March',
                          'April',
                          'May',
                          'June',
                          'July',
                          'August',
                          'September',
                          'October',
                          'November',
                          'December',
                        ];
                        const indexA = monthOrder.findIndex((m) =>
                          dateA.includes(m),
                        );
                        const indexB = monthOrder.findIndex((m) =>
                          dateB.includes(m),
                        );
                        return indexB - indexA;
                      }

                      return 0;
                    })
                    .map(([date, items]) => {
                      const filteredItems = filterItems(items);
                      if (filteredItems.length === 0) return null;

                      return (
                        <div key={date} className="mb-4">
                          <h3 className="text-sm font-medium text-muted-foreground mb-1.5 px-6">
                            {date}
                          </h3>
                          <div>
                            {filteredItems.map((item, index) =>
                              renderHistoryItem(item, index),
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              );
            })()}
          </div>

          {/* Ask AI Panel - Auto-enabled when history loads */}
          {!loading && !error && teamId && recentVideoFileIds.length > 0 && (
            <div
              className={cn(
                'flex-shrink-0',
                isMobile ? 'w-full' : 'w-[350px]',
                isMobile && !showAskAI ? 'hidden' : '',
              )}
            >
              <HistoryAskAI fileIds={recentVideoFileIds} teamId={teamId} />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HistoryModal;
